import { useState } from "react";
import { toast } from "sonner";
import { Home, ChevronDown, Waves, Landmark, Mountain, UtensilsCrossed, Check, BellRing, Loader2 } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { http, formatErr } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

export const CITY_SEGMENTS = [
  ["seg_beach", Waves, ["Bali", "Lombok", "Labuan Bajo", "Belitung", "Raja Ampat"]],
  ["seg_culture", Landmark, ["Yogyakarta", "Solo", "Toraja", "Danau Toba"]],
  ["seg_nature", Mountain, ["Bromo & Malang", "Bandung", "Banyuwangi (Ijen)", "Bukit Lawang"]],
  ["seg_city", UtensilsCrossed, ["Jakarta", "Medan", "Makassar", "Surabaya"]],
];
const slug = (c) => c.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "");
const Soon = () => { const { t } = useLang(); return <span className="ml-auto rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wide text-amber-800">{t("cs_soon")}</span>; };

function WaitlistDialog({ city, onClose }) {
  const { t, lang } = useLang();
  const [f, setF] = useState({ phone: "", name: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await http.post("/city-waitlist", { ...f, city: slug(city), lang });
      toast.success(t(data.status === "already" ? "wl_already" : "wl_done").replace("{city}", city));
      onClose();
    } catch (err) { toast.error(formatErr(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="waitlist-dialog" className="booking-dialog rounded-3xl sm:max-w-sm" closeLabel={t("mobile_close")} aria-describedby={undefined}>
        <DialogHeader className="booking-dialog-header"><DialogTitle className="flex items-center gap-2 pr-12 text-lg"><BellRing className="h-5 w-5 shrink-0 text-[#D97706]" />{t("wl_title").replace("{city}", city)}</DialogTitle></DialogHeader>
        <div className="booking-dialog-scroll space-y-4">
        <p className="text-sm text-slate-500">{t("wl_sub")}</p>
        <form onSubmit={submit} className="space-y-3">
          <Input data-testid="waitlist-phone-input" type="tel" inputMode="tel" autoComplete="tel" aria-label={t("phone")} required pattern="\+?[0-9 \-]{8,20}" placeholder="+62 812 xxxx xxxx" value={f.phone}
            onInvalid={(e) => e.target.setCustomValidity(t("pt_phone_invalid"))}
            onChange={(e) => { e.target.setCustomValidity(e.target.value.replace(/\D/g, "").length < 8 && e.target.value ? t("pt_phone_invalid") : ""); setF({ ...f, phone: e.target.value }); }} />
          <Input data-testid="waitlist-name-input" autoComplete="name" aria-label={t("full_name")} maxLength={80} placeholder={t("wl_name")} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
          <button disabled={busy} data-testid="waitlist-submit-btn" className="flex w-full items-center justify-center gap-2 rounded-full bg-[#E11D48] py-3 font-bold text-white transition-colors hover:bg-[#BE123C] disabled:opacity-60">{busy && <Loader2 className="h-4 w-4 animate-spin" />}{t("wl_submit")}</button>
        </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function CityMenu() {
  const { t } = useLang();
  const [city, setCity] = useState(null);
  return (
    <>
    {city && <WaitlistDialog city={city} onClose={() => setCity(null)} />}
    <DropdownMenu>
      <DropdownMenuTrigger data-testid="city-menu-trigger" title={t("cs_menu")} className="flex shrink-0 items-center gap-1 rounded-full border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-bold text-slate-700 transition-shadow hover:shadow-md">
        <Home className="h-4 w-4 text-[#064E3B]" /><span className="hidden sm:inline">Sumbar</span><ChevronDown className="h-3.5 w-3.5 text-slate-400" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" data-testid="city-menu" className="max-h-[70vh] w-64 overflow-y-auto">
        <DropdownMenuItem data-testid="city-sumatera-barat"><Check className="mr-2 h-4 w-4 text-emerald-600" /><b>Sumatera Barat</b><span className="ml-auto rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-extrabold uppercase text-emerald-800">{t("cs_active")}</span></DropdownMenuItem>
        {CITY_SEGMENTS.map(([k, Icon, cities]) => (
          <div key={k}>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-slate-500"><Icon className="h-3.5 w-3.5" />{t(k)}</DropdownMenuLabel>
            {cities.map((c) => <DropdownMenuItem key={c} onSelect={() => setCity(c)} data-testid={`city-${slug(c)}`} className="cursor-pointer text-sm">{c}<Soon /></DropdownMenuItem>)}
          </div>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
    </>
  );
}

export default function ComingCities() {
  const { t } = useLang();
  const [city, setCity] = useState(null);
  return (
    <section data-testid="coming-cities-section" className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
      {city && <WaitlistDialog city={city} onClose={() => setCity(null)} />}
      <div className="rounded-[2rem] bg-[#064E3B] p-6 text-white sm:p-10">
        <p className="inline-flex rounded-full bg-[#FBBF24] px-4 py-1.5 text-xs font-extrabold uppercase tracking-wider text-slate-900">{t("cs_soon")}</p>
        <h2 className="mt-4 text-3xl font-bold tracking-tight sm:text-4xl">{t("cs_title")}</h2>
        <p className="mt-2 max-w-2xl text-emerald-100">{t("cs_sub")}</p>
        <p data-testid="coming-cities-hint" className="mt-1 flex items-center gap-1.5 text-sm font-semibold text-[#FBBF24]"><BellRing className="h-4 w-4" />{t("cs_notify_hint")}</p>
        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {CITY_SEGMENTS.map(([k, Icon, cities]) => (
            <div key={k} data-testid={`coming-seg-${k}`} className="rounded-2xl border border-white/15 bg-white/5 p-5 transition-colors hover:bg-white/10">
              <p className="flex items-center gap-2 font-bold"><Icon className="h-5 w-5 text-[#FBBF24]" />{t(k)}</p>
              <ul className="mt-3 space-y-2 text-sm">{cities.map((c) => (
                <li key={c}><button type="button" onClick={() => setCity(c)} data-testid={`coming-city-${slug(c)}`} className="group flex w-full items-center justify-between gap-2 rounded-lg px-1 py-0.5 text-left text-emerald-50 transition-colors hover:bg-white/10">{c}
                  <span className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#FBBF24] transition-colors group-hover:bg-[#FBBF24] group-hover:text-slate-900"><BellRing className="h-3 w-3" />{t("cs_soon")}</span></button></li>
              ))}</ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
